import { Multiselect, SelectProps } from '@cloudscape-design/components';
import { memo, useEffect, useMemo, useState } from 'react';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';
import { convertList } from 'src/utils/generalUtils';

interface MultiSelectTemplateProps {
  'templates': AuditTemplate[];
  'isLoading'?: boolean;
  'readOnly'?: boolean;
  'error'?: string | null;
  'selectedTemplateIds': string[];
  'onSelectionChange': (selectedIds: string[]) => void;
  'placeholder'?: string;
  'data-testid'?: string;
}

const MultiSelectTemplate = ({
  templates,
  isLoading = false,
  readOnly = false,
  error,
  selectedTemplateIds,
  onSelectionChange,
  placeholder = 'Select templates',
  'data-testid': testId = 'template-multiselect',
}: MultiSelectTemplateProps) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectProps.Option[]>([]);

  useEffect(() => {
    setSelectedOptions(
      convertList<AuditTemplate, SelectProps.Option>(
        templates.filter((template) => selectedTemplateIds.includes(template.id)),
        (item) => ({
          value: item.id,
          label: item.templateName,
        }),
      ),
    );
  }, [templates, selectedTemplateIds]);

  const statusType = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    return 'finished';
  }, [isLoading, error]);

  const templateOptions = useMemo(() => {
    return convertList<AuditTemplate, SelectProps.Option>(templates, (item) => ({
      value: item.id,
      label: item.templateName,
    }));
  }, [templates]);

  return (
    <Multiselect
      readOnly={readOnly}
      data-testid={testId}
      selectedOptions={selectedOptions}
      onChange={({ detail }) => {
        const values = detail.selectedOptions as SelectProps.Option[];
        onSelectionChange(
          convertList<SelectProps.Option, string>(values, (item) => item.value || ''),
        );
        setSelectedOptions(values);
      }}
      filteringType="auto"
      options={templateOptions}
      errorText={error || undefined}
      loadingText="Loading templates"
      statusType={statusType}
      placeholder={placeholder}
    />
  );
};

export default memo(MultiSelectTemplate);
